import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  signInAnonymously,
  linkWithCredential,
  EmailAuthProvider,
  User,
  GoogleAuthProvider,
  AuthError,
  Auth,
  signInWithCredential,
  linkWithPopup,
} from 'firebase/auth';
import { auth, db } from '@/config/firebase';
import { googleProvider } from '../providers';
import { handleAuthError } from '@/lib/utils/errors/handlers';
import { createUserProfile } from '@/lib/db/services/userService';
import { collection, doc, getDocs, runTransaction } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';

const isAuthError = (error: unknown): error is AuthError => {
  return error instanceof Error && 'code' in error;
};

const transferUserData = async (
  anonymousUid: string,
  permanentUid: string
): Promise<void> => {
  try {
    if (!anonymousUid || !permanentUid) {
      throw new Error('Both anonymousUid and permanentUid are required');
    }

    if (anonymousUid === permanentUid) {
      throw new Error('Source and destination UIDs cannot be the same');
    }

    const subcollections = ['subscriptions', 'payments', 'checkout_sessions'];

    await runTransaction(db, async (transaction) => {
      // Transfer main user document
      const anonymousUserRef = doc(db, 'users', anonymousUid);
      const anonymousUserDoc = await transaction.get(anonymousUserRef);

      if (!anonymousUserDoc.exists()) {
        throw new Error(
          `No user data found for anonymous UID: ${anonymousUid}`
        );
      }

      const permanentUserRef = doc(db, 'users', permanentUid);
      const permanentUserDoc = await transaction.get(permanentUserRef);

      if (permanentUserDoc.exists()) {
        throw new Error(
          `User data already exists for permanent UID: ${permanentUid}`
        );
      }

      const userData = anonymousUserDoc.data();
      const updatedUserData = {
        ...userData,
        uid: permanentUid,
        lastUpdated: new Date(),
      };

      // Set main user document
      transaction.set(permanentUserRef, updatedUserData);

      // Transfer subcollections
      for (const subcollName of subcollections) {
        const subcollRef = collection(db, 'users', anonymousUid, subcollName);
        const subcollDocs = await getDocs(subcollRef);

        subcollDocs.forEach((docSnapshot) => {
          const newDocRef = doc(
            db,
            'users',
            permanentUid,
            subcollName,
            docSnapshot.id
          );
          transaction.set(newDocRef, {
            ...docSnapshot.data(),
            userId: permanentUid,
            lastUpdated: new Date(),
          });

          const oldDocRef = doc(
            db,
            'users',
            anonymousUid,
            subcollName,
            docSnapshot.id
          );
          transaction.delete(oldDocRef);
        });
      }

      // Delete the old user document after all subcollections are transferred
      transaction.delete(anonymousUserRef);

      console.log(
        `Transferred user data and subcollections from ${anonymousUid} to ${permanentUid}`
      );
    });
  } catch (error) {
    if (error instanceof FirebaseError) {
      console.error(`Firebase error during transfer: ${error.code}`, error);
      throw error;
    }

    console.error('Error during user data transfer:', error);
    throw error;
  }
};

interface ConversionResult {
  user: User;
  isNewUser: boolean;
  previousUid?: string;
}

const handleExistingCredential = async (
  error: AuthError,
  currentUser: User,
  auth: Auth
): Promise<ConversionResult> => {
  const existingCredential = GoogleAuthProvider.credentialFromError(error);

  if (!existingCredential) {
    throw new Error('No existing credential found');
  }

  try {
    const signInResult = await signInWithCredential(auth, existingCredential);
    const previousUid = currentUser.uid;

    await transferUserData(previousUid, signInResult.user.uid);

    return {
      user: signInResult.user,
      isNewUser: false,
      previousUid,
    };
  } catch (signInError) {
    throw new Error(
      `Error signing in with existing Google account: ${signInError}`
    );
  }
};

export async function signUp(email: string, password: string): Promise<User> {
  try {
    const { user } = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    await createUserProfile(user);
    return user;
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function signIn(email: string, password: string): Promise<User> {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function signInWithGoogle(): Promise<User> {
  try {
    let result;
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      result = await convertAnonymousUserFromGoogleProvider();
    } else {
      result = await signInWithPopup(auth, googleProvider);
    }
    await createUserProfile(result.user);
    return result.user;
  } catch (error) {
    if (isAuthError(error)) {
      const originalError = error.details?.originalError as
        | AuthError
        | undefined;
      if (originalError) {
        error = originalError;
      }
      if (error.code === 'auth/credential-already-in-use' && auth.currentUser) {
        const result = await handleExistingCredential(
          error,
          auth.currentUser,
          auth
        );
        return result.user;
      }
    }
    throw handleAuthError(error);
  }
}

export async function signInAsAnonymous(): Promise<User> {
  try {
    const { user } = await signInAnonymously(auth);
    return user;
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function convertAnonymousUser(
  email: string,
  password: string
): Promise<User> {
  const currentUser = auth.currentUser;

  if (!currentUser?.isAnonymous) {
    throw new Error('User is not anonymous');
  }

  try {
    const credential = EmailAuthProvider.credential(email, password);
    const { user } = await linkWithCredential(currentUser, credential);
    await createUserProfile(user);
    return user;
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function convertAnonymousUserFromGoogleProvider() {
  const currentUser = auth.currentUser;

  if (!currentUser?.isAnonymous) {
    throw new Error('User is not anonymous');
  }

  try {
    return await linkWithPopup(currentUser, googleProvider);
  } catch (error) {
    throw handleAuthError(error);
  }
}

export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    throw handleAuthError(error);
  }
}
